import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import FaceCapture from '../components/FaceCapture';
import QRCodeDisplay from '../components/QRCodeDisplay';
import processImage from '../api/processImage';
import generateQRCode from '../api/generateQRCode';
import convertImage from '../api/convertImage';
import { ChevronDown, ChevronUp, Copy, Download, Settings } from 'lucide-react';
import convertToCbor from '../api/convertToCbor';
import cwtSigning from '../api/cwtSigning';
import { useSettings } from '../hooks/useSettings';

const Claim169CardGenerator: React.FC = () => {
  const history = useHistory();
  const { settings } = useSettings();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [convertedImage, setConvertedImage] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<number | null>(null); // Input image size
  const [processedImageSize, setProcessedImageSize] = useState<number | null>(null); // Processed image size
  const [convertedImageSize, setConvertedImageSize] = useState<number | null>(null); // Converted image size
  const [demographics, setDemographics] = useState<any>({});
  const [qrCodeData, setQrCodeData] = useState<string | undefined>(undefined);
  const [format, setFormat] = useState<'webp' | 'avif'>('webp');
  const [colorMode, setColorMode] = useState<'grayscale' | 'color'>('color');
  const [imageWidth, setImageWidth] = useState<number>(96);
  const [imageHeight, setImageHeight] = useState<number>(96);
  const [maxBytes, setMaxBytes] = useState<number>(900);
  const [initialJson, setInitialJson] = useState<string | null>(null);
  const [isJsonExpanded, setIsJsonExpanded] = useState(false);
  const [cborHex, setCborHex] = useState<string | null>(null);
  const [isCborExpanded, setIsCborExpanded] = useState(false); // State for collapsible CBOR section

  // State for collapsible sections
  const [isInputImageExpanded, setIsInputImageExpanded] = useState(true);
  const [isProcessedImageExpanded, setIsProcessedImageExpanded] = useState(false);
  const [isConvertedImageExpanded, setIsConvertedImageExpanded] = useState(false);


  const [cwtData, setCwtData] = useState<string | null>(null); // State for signed CWT data
  const [isCwtExpanded, setIsCwtExpanded] = useState(false); // State for collapsible CWT section

  const [progress, setProgress] = useState<number>(0); // Progress percentage
  const [progressMessage, setProgressMessage] = useState<string>(''); // Current processing stage
  const [isProcessing, setIsProcessing] = useState<boolean>(false); // Whether processing is ongoing

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false); // State for modal visibility

  const qrCodeRef = React.useRef<HTMLDivElement | null>(null); // Reference for the QR Code section
  const facePreviewRef = React.useRef<HTMLDivElement | null>(null); // Reference for the Face Preview section
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false); // State to track camera activity
  const [qrCodeVersion, setQrCodeVersion] = useState<string | undefined>(undefined); // State for QR code version

  const handleCameraStateChange = (isActive: boolean) => {
    setIsCameraActive(isActive); // Update the state when the camera state changes
  };

  const calculateContentSize = (content: string | null): string => {
    if (!content) return 'N/A';
    const sizeInBytes = new Blob([content]).size;
    return sizeInBytes < 1024
      ? `${sizeInBytes} bytes`
      : `${(sizeInBytes / 1024).toFixed(2)} KB`;
  };

  /**
 * Calculates the binary size of a hexadecimal string.
 * @param hexString - The hexadecimal string.
 * @returns The binary size in bytes.
 */
  const calculateBinarySize = (hexString: string | null): string => {
    if (!hexString) return 'N/A';
    const binarySize = Math.ceil(hexString.length / 2); // Each 2 hex characters = 1 byte
    return `${binarySize} bytes`;
  };

  const handleImageCapture = (src: string | null) => {
    setImageSrc(src);
    setConvertedImage(null);
    setQrCodeData(undefined);
    setQrCodeVersion(undefined)
    setProcessedImage(null);

    if (src) {
      const base64Length = src.split(',')[1].length;
      const sizeInBytes = (base64Length * 3) / 4 - (src.endsWith('==') ? 2 : src.endsWith('=') ? 1 : 0);
      setImageSize(sizeInBytes); // Calculate input image size
      setIsInputImageExpanded(true); // Expand Input Image section
      setIsProcessedImageExpanded(false); // Collapse other sections
      setIsConvertedImageExpanded(false);
      if (facePreviewRef.current) {
        facePreviewRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        setImageSrc(result);
        setConvertedImage(null);
        setQrCodeData(undefined);
        setProcessedImage(null);
        setImageSize(file.size); // Set input image size from file
        setIsInputImageExpanded(true); // Expand Input Image section
        setIsProcessedImageExpanded(false); // Collapse other sections
        setIsConvertedImageExpanded(false);

        if (facePreviewRef.current) {
          facePreviewRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const byteArrayToHexString = (byteArray: Uint8Array): string => {
    return Array.from(byteArray)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  };

  const handleGenerateQRCode = async () => {
    if (imageSrc && demographics) {
      setIsModalOpen(true); // Open the modal
      setIsProcessing(true); // Start processing
      setProgress(10);
      setProgressMessage('Processing image...');

      // Process the image and get it as a byte array
      processImage(imageSrc, format, colorMode, imageWidth, imageHeight, maxBytes)
        .then((processedImage) => {
          setProgress(30);
          setProgressMessage('Image processed successfully.');

          const processedBlob = new Blob([processedImage], { type: `image/${format}` });
          setProcessedImage(URL.createObjectURL(processedBlob));
          setProcessedImageSize(processedBlob.size); // Set processed image size
          setIsProcessedImageExpanded(true); // Expand Processed Image section
          setIsConvertedImageExpanded(false);

          // Convert the processed image to PNG
          convertImage(processedImage, format, 'png')
            .then((convertedImage => {
              const convertedBlob = new Blob([convertedImage], { type: 'image/png' });
              const convertedImageUrl = URL.createObjectURL(convertedBlob);
              setConvertedImage(convertedImageUrl);
              setConvertedImageSize(convertedBlob.size); // Set converted image size
              //setIsProcessedImageExpanded(false); // Collapse Processed Image section
              setIsConvertedImageExpanded(true); // Expand Converted Image section
            })).catch((error) => {
              setIsProcessing(false); // End processing
              setIsModalOpen(false); // Close the modal
              console.error('Error generating QR code:', error);
              alert('Error generating QR code. Please try again.');
            });

          setProgressMessage('Generating JSON data...');

          // Convert the processed image to a hex string
          const processedImageHex = byteArrayToHexString(new Uint8Array(processedImage));

          // Construct the JSON object
          const jsonObject = {
            id: demographics.id || undefined,
            fullName: demographics.name || undefined,
            dob: demographics.dob || undefined,
            gender: demographics.gender || undefined,
            address: demographics.address || undefined,
            email: demographics.email || undefined,
            phone: demographics.phone || undefined,
            nationality: demographics.nationality || undefined,
            face: {
              data: processedImageHex,
              dataFormat: "image",
              dataSubFormat: format,
            },
          };

          // Beautify the JSON and set it to state
          const jsonString = JSON.stringify(jsonObject, null, 2);
          setInitialJson(jsonString);
          setIsJsonExpanded(true); // Expand JSON section
          setProgress(40);
          setProgressMessage('JSON data generated.');
          setProgressMessage('Converting JSON to CBOR...');
          convertToCbor(jsonString).then(cborData => {
            setProgress(60);
            setProgressMessage('CBOR data generated.');
            setProgressMessage('Signing CBOR data...');
            const cborHex = cborData[1];  // Get the CBOR hex string
            setCborHex(cborHex); // Set CBOR hex data
            setIsCborExpanded(true); // Expand CBOR section
            cwtSigning(cborHex).then(cwtData => {
              setProgress(80);
              setProgressMessage('CBOR data signed.');
              setProgressMessage('Generating QR code...');
              setCwtData(cwtData); // Set signed CWT data
              setIsCwtExpanded(true); // Expand CWT section


              // Generate the QR code
              generateQRCode(cwtData)
                .then((qrCodeResult) => {
                  setProgress(100);
                  setProgressMessage('QR code generated successfully.');
                  setIsModalOpen(false); // Close the modal
                  setQrCodeData(qrCodeResult[2]); // Set QR code data
                  setQrCodeVersion(qrCodeResult[0]); // Set QR code version
                  setIsConvertedImageExpanded(false); // Collapse Converted Image section 

                  // Scroll to the QR Code section
                  if (qrCodeRef.current) {
                    qrCodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }
                ).catch((error) => {
                  setIsProcessing(false); // End processing
                  setIsModalOpen(false); // Close the modal
                  console.error('Error generating QR code:', error);
                  alert('Error generating QR code. Please try again.');
                });
            }
            ).catch((error) => {
              setIsProcessing(false); // End processing
              setIsModalOpen(false); // Close the modal
              console.error('Error signing CWT:', error);
              alert('Error signing CWT. Please try again.');
            });

          });
        }).catch((error) => {
          setIsProcessing(false); // End processing
          setIsModalOpen(false); // Close the modal
          console.error('Error generating QR code:', error);
          alert('Error generating QR code. Please try again.');
        });
    };
  }

  const formatSize = (size: number | null): string => {
    if (size === null) return 'N/A';
    return size >= 1024 ? `${(size / 1024).toFixed(2)} KB` : `${size} bytes`;
  };


  const handleGenderKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
    const key = e.key.toLowerCase();
    let gender = '';
    if (key === 'm') {
      gender = 'male';
    } else if (key === 'f') {
      gender = 'female';
    } else if (key === 'o') {
      gender = 'other';
    }
    if (gender !== '') {
      setDemographics({ ...demographics, gender: gender });
    }
  };


  return (
    <>
      {/* Modal Component */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="text-center">
          {isProcessing ? (
            <>
              <h3 id="progress-modal-title" className="text-lg font-semibold mb-4">
                {progressMessage}
              </h3>
              <Progress value={progress} className="mb-4" />
              <p id="progress-modal-description" className="text-sm text-muted-foreground mt-2">
                {progress}%
              </p>
            </>
          ) : (
            <>
              <h3 id="progress-modal-title" className="text-lg font-semibold mb-4">
                QR Code Generated
              </h3>
              <Button
                onClick={() => setIsModalOpen(false)}
                className="mt-2"
              >
                Close
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Main Component UI */}
      <div className="p-4">
        {/* Title Bar */}
        <div className="bg-primary text-primary-foreground p-4 text-center rounded-md mb-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold flex-1">OORU Claim-169 QR-Code Generator</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => history.push('/settings')}
            className="text-primary-foreground hover:bg-primary/80"
            title="Settings"
          >
            <Settings className="h-5 w-5" />
          </Button>
        </div>

        {/* Input Image Section */}
        <div className="flex items-center justify-between bg-muted text-muted-foreground p-2 rounded-md">
          <h3 className="text-base font-medium">Face Image</h3>
        </div>
        <div className={`mb-4 flex justify-center items-center ${(imageSrc || isCameraActive) ? 'flex-col' : 'flex-row'} ${settings.cameraEnabled ? 'gap-4' : 'gap-0'}`}>
          {settings.cameraEnabled && (
            <>
              <FaceCapture onImageCapture={handleImageCapture} onCameraStateChange={handleCameraStateChange} />
              <p className="text-base">/</p>
            </>
          )}
          <div className="flex flex-col justify-start items-center w-full h-full bg-background overflow-hidden">
            <div className="flex flex-row m-2.5 p-2.5 items-center justify-center">
              <p className="text-base">Select an image: &nbsp;</p>
              <input type="file" accept="image/png, image/jpeg" onChange={handleFileSelect} />
            </div>

            <Collapsible open={isInputImageExpanded}>
              <CollapsibleContent>
                {imageSrc && (
                  <div
                    ref={facePreviewRef}
                    className="flex flex-col items-center justify-center"
                  >
                    <p className="text-base mb-2">Face Image Preview</p>
                    <img
                      src={imageSrc}
                      alt="Input Preview"
                      className="w-full max-w-[300px] max-h-[300px] h-auto mb-4"
                    />
                    <p className="text-sm text-muted-foreground">
                      Size: {formatSize(imageSize)}
                    </p>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Side: Input */}
          <div className="border border-border p-0 h-full">
            {/* Input Section Title */}
            <div className="bg-muted text-foreground p-2 rounded-md text-center">
              <h2 className="text-lg font-semibold">Demographics</h2>
            </div>
            <div className="p-4 h-full">
              <div className="grid grid-cols-2 gap-4 mt-2">
                {/*ID Input */}
                <div>
                  <Label htmlFor="id-input">ID *</Label>
                  <Input
                    id="id-input"
                    className="w-full"
                    onChange={(e) => setDemographics({ ...demographics, id: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="name-input">Full Name *</Label>
                  <Input
                    id="name-input"
                    className="w-full"
                    onChange={(e) => setDemographics({ ...demographics, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="dob-input">Date of Birth *</Label>
                  <Input
                    id="dob-input"
                    type="date"
                    className="w-full"
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setDemographics({ ...demographics, dob: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="address-input">Address</Label>
                  <Input
                    id="address-input"
                    className="w-full"
                    onChange={(e) => setDemographics({ ...demographics, address: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="phone-input">Phone</Label>
                  <Input
                    id="phone-input"
                    type="tel"
                    className="w-full"
                    onChange={(e) => setDemographics({ ...demographics, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="nationality-input">Nationality</Label>
                  <Input
                    id="nationality-input"
                    className="w-full"
                    onChange={(e) => setDemographics({ ...demographics, nationality: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="email-input">Email</Label>
                  <Input
                    id="email-input"
                    type="email"
                    className="w-full"
                    onChange={(e) => setDemographics({ ...demographics, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="gender-select">Gender</Label>
                  <Select
                    id="gender-select"
                    value={demographics.gender || ''}
                    onChange={(e) => setDemographics({ ...demographics, gender: e.target.value })}
                    onKeyDown={handleGenderKeyDown}
                    className="w-full"
                  >
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Select>
                </div>
              </div>
              <div className="my-4">
                <Label htmlFor="format-select">Select Image Format</Label>
                <Select
                  id="format-select"
                  value={format}
                  onChange={(e) => setFormat(e.target.value as 'webp' | 'avif')}
                  className="w-full"
                >
                  <option value="webp">WEBP</option>
                  <option value="avif">AVIF</option>
                </Select>
              </div>
              <div className="my-4">
                <Label htmlFor="color-mode-select">Select Color Mode</Label>
                <Select
                  id="color-mode-select"
                  value={colorMode}
                  onChange={(e) => setColorMode(e.target.value as 'grayscale' | 'color')}
                  className="w-full"
                >
                  <option value="color">Color (24-bit RGB)</option>
                  <option value="grayscale">Grayscale (Smaller file size)</option>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4 my-4">
                <div>
                  <Label htmlFor="width-input">Image Width (pixels)</Label>
                  <Input
                    id="width-input"
                    type="number"
                    value={imageWidth}
                    onChange={(e) => setImageWidth(parseInt(e.target.value) || 96)}
                    min={16}
                    max={512}
                    step={1}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Default: 96</p>
                </div>
                <div>
                  <Label htmlFor="height-input">Image Height (pixels)</Label>
                  <Input
                    id="height-input"
                    type="number"
                    value={imageHeight}
                    onChange={(e) => setImageHeight(parseInt(e.target.value) || 96)}
                    min={16}
                    max={512}
                    step={1}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Default: 96</p>
                </div>
              </div>
              <div className="my-4">
                <Label htmlFor="max-bytes-input">Max Bytes</Label>
                <Input
                  id="max-bytes-input"
                  type="number"
                  value={maxBytes}
                  onChange={(e) => setMaxBytes(parseInt(e.target.value) || 900)}
                  min={100}
                  max={10000}
                  step={10}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground mt-1">Maximum file size in bytes. Default: 900</p>
              </div>
              <Button
                onClick={handleGenerateQRCode}
                className="w-full my-4"
                disabled={!imageSrc || !demographics.id || !demographics.name || !demographics.dob}
              >
                Get QR Code
              </Button>
            </div>
          </div>

          {/* Right Side: Preview */}
          <div className="border border-border h-full">
            <div className="bg-muted text-foreground p-2 rounded-md text-center">
              <h2 className="text-lg font-semibold">Output</h2>
            </div>
            <div className="p-4 h-full">

                {/* Processed Image Section Title */}
                <Collapsible open={isProcessedImageExpanded} onOpenChange={setIsProcessedImageExpanded}>
                  <div className="flex items-center justify-between bg-muted text-muted-foreground p-2 rounded-md mt-4">
                    <h3 className="text-base font-medium">Processed Image ({format.toUpperCase()})</h3>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon">
                        {isProcessedImageExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    {processedImage && (
                      <div className="mt-2">
                        <a
                          href={processedImage}
                          download={`processed-image.${format}`}
                          className="text-primary hover:underline"
                        >
                          Download Processed Image ({format.toUpperCase()})
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          Size: {formatSize(processedImageSize)}
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Converted Image Section Title */}
                <Collapsible open={isConvertedImageExpanded} onOpenChange={setIsConvertedImageExpanded}>
                  <div className="flex items-center justify-between bg-muted text-muted-foreground p-2 rounded-md mt-4">
                    <h3 className="text-base font-medium">Converted Image (PNG)</h3>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon">
                        {isConvertedImageExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    {convertedImage && (
                      <div className="mt-2">
                        <img
                          src={convertedImage}
                          alt="Converted Preview"
                          className="w-full max-w-[300px] max-h-[300px] h-auto mb-4"
                        />
                        <a
                          href={convertedImage}
                          download="converted-image.png"
                          className="text-primary hover:underline block"
                        >
                          Download Converted Image (PNG)
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          Size: {formatSize(convertedImageSize)}
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>


                {/* Initial JSON Section Title */}
                <Collapsible open={isJsonExpanded} onOpenChange={setIsJsonExpanded}>
                  <div className="flex items-center justify-between bg-muted text-muted-foreground p-2 rounded-md mt-4">
                    <h3 className="text-base font-medium">Initial JSON</h3>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (initialJson) {
                            navigator.clipboard.writeText(initialJson);
                            alert('Copied to clipboard!');
                          }
                        }}
                        title="Copy"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (initialJson) {
                            const blob = new Blob([initialJson], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = 'initial-json.json';
                            link.click();
                            URL.revokeObjectURL(url);
                          }
                        }}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="icon">
                          {isJsonExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  </div>
                  <CollapsibleContent>
                    {initialJson && (
                      <div className="mt-2">
                        <pre className="bg-muted p-4 rounded-md overflow-y-auto overflow-wrap-break whitespace-pre-wrap max-h-[300px]">
                          {initialJson}
                        </pre>
                        <p className="text-sm text-muted-foreground mt-2">
                          Size: {calculateContentSize(initialJson)}
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* CBOR Data Section Title */}
                <Collapsible open={isCborExpanded} onOpenChange={setIsCborExpanded}>
                  <div className="flex items-center justify-between bg-muted text-muted-foreground p-2 rounded-md mt-4">
                    <h3 className="text-base font-medium">Cbor Data</h3>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (cborHex) {
                            navigator.clipboard.writeText(cborHex);
                            alert('Copied to clipboard!');
                          }
                        }}
                        title="Copy"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (cborHex) {
                            const blob = new Blob([cborHex], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = 'cbor-data.txt';
                            link.click();
                            URL.revokeObjectURL(url);
                          }
                        }}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="icon">
                          {isCborExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  </div>
                  <CollapsibleContent>
                    {cborHex && (
                      <div className="mt-2">
                        <pre className="bg-muted p-4 rounded-md overflow-y-auto overflow-wrap-break whitespace-pre-wrap max-h-[300px]">
                          {cborHex}
                        </pre>
                        <p className="text-sm text-muted-foreground mt-2">
                          Size: {calculateContentSize(cborHex)}
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* CWT Data Section */}
                <Collapsible open={isCwtExpanded} onOpenChange={setIsCwtExpanded}>
                  <div className="flex items-center justify-between bg-muted text-muted-foreground p-2 rounded-md mt-4">
                    <h3 className="text-base font-medium">CWT Data</h3>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (cwtData) {
                            navigator.clipboard.writeText(cwtData);
                            alert('Copied to clipboard!');
                          }
                        }}
                        title="Copy"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (cwtData) {
                            const blob = new Blob([cwtData], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = 'cwt-data.txt';
                            link.click();
                            URL.revokeObjectURL(url);
                          }
                        }}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="icon">
                          {isCwtExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  </div>
                  <CollapsibleContent>
                    {cwtData && (
                      <div className="mt-2">
                        <pre className="bg-muted p-4 rounded-md overflow-y-auto overflow-wrap-break whitespace-pre-wrap max-h-[300px]">
                          {cwtData}
                        </pre>
                        <p className="text-sm text-muted-foreground mt-2">
                          CWT Hex Size: {calculateContentSize(cwtData)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          CWT Binary Size: {calculateBinarySize(cwtData)}
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* QR Code Section */}
                <div className="flex items-center justify-between bg-muted text-muted-foreground p-2 rounded-md mt-4">
                  <h3 className="text-base font-medium">QR Code</h3>
                </div>
                <div ref={qrCodeRef}>
                  {qrCodeData && (
                    <QRCodeDisplay
                      qrCodeData={qrCodeData}
                      qrCodeVersion={qrCodeVersion} // Pass the QR code version
                      demographics={{
                        fullName: demographics.name || '',
                        dob: demographics.dob || '',
                        gender: demographics.gender || '',
                        address: demographics.address || '',
                        photo: imageSrc, // Use the captured image as the photo
                      }}
                    />
                  )}
                </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Claim169CardGenerator;